import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useThemeProvider } from '../utils/ThemeContext';
import { chartColors } from './ChartjsConfig';
import { Chart, LineController, LineElement, Filler, PointElement, LinearScale, TimeScale, CategoryScale, Tooltip, Legend } from 'chart.js';
import 'chartjs-adapter-moment';
import { tailwindConfig, formatValue } from '../utils/Utils';

Chart.register(LineController, LineElement, Filler, PointElement, LinearScale, TimeScale, CategoryScale, Tooltip, Legend);

function LineChart02({ data, width, height, options: customOptions }) {
  const [chart, setChart] = useState(null);
  const canvas = useRef(null);
  const { currentTheme } = useThemeProvider();
  const darkMode = currentTheme === 'dark';
  const { textColor, gridColor, tooltipBodyColor, tooltipBgColor, tooltipBorderColor } = chartColors;

  // Default options that can be overridden
  const defaultOptions = useMemo(() => ({
    layout: { padding: 20 },
    scales: {
      y: {
        border: { display: false },
        beginAtZero: true,
        ticks: {
          maxTicksLimit: 5,
          callback: (value) => formatValue(value).replace('$', ''),
          color: darkMode ? textColor.dark : textColor.light,
        },
        grid: { color: darkMode ? gridColor.dark : gridColor.light },
      },
      x: {
        type: 'time',
        time: {
          parser: 'YYYY-MM-DD',
          unit: 'day',
          displayFormats: { day: 'MMM D' },
        },
        border: { display: false },
        grid: { display: false },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          color: darkMode ? textColor.dark : textColor.light,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (context) => {
            const date = new Date(context[0].label);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          },
          label: (context) => {
            return `${context.dataset.label}: ${formatValue(context.parsed.y).replace('$', '')}`;
          },
        },
        titleColor: tooltipBodyColor.light,
        bodyColor: darkMode ? tooltipBodyColor.dark : tooltipBodyColor.light,
        backgroundColor: darkMode ? tooltipBgColor.dark : tooltipBgColor.light,
        borderColor: darkMode ? tooltipBorderColor.dark : tooltipBorderColor.light,
      },
    },
    interaction: {
      intersect: false,
      mode: 'nearest',
    },
    maintainAspectRatio: false,
    resizeDelay: 200,
  }), [darkMode, textColor, gridColor, tooltipBodyColor, tooltipBgColor, tooltipBorderColor]);

  // Merge custom options with defaults, giving priority to custom options
  const mergedOptions = useMemo(() => {
    if (!customOptions) return defaultOptions;
    
    // Deep merge function
    const deepMerge = (target, source) => {
      const result = { ...target };
      for (const key in source) {
        if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
          result[key] = deepMerge(target[key] || {}, source[key]);
        } else {
          result[key] = source[key];
        }
      }
      return result;
    };

    const merged = deepMerge(defaultOptions, customOptions);
    // If custom scale is category, remove default time scale config so labels show correctly
    if (merged.scales?.x?.type === 'category' && merged.scales.x.time) {
      const { time, ...xRest } = merged.scales.x;
      merged.scales.x = xRest;
    }
    return merged;
  }, [defaultOptions, customOptions]);

  useEffect(() => {
    const ctx = canvas.current;
    if (!ctx) return;

    const newChart = new Chart(ctx, {
      type: 'line',
      data: data,
      options: mergedOptions,
    });

    setChart(newChart);
    return () => {
      if (newChart && !newChart.destroyed) {
        newChart.destroy();
      }
    };
  }, [data, mergedOptions]);

  useEffect(() => {
    if (!chart || chart.destroyed) return;

    try {
      if (darkMode) {
        chart.options.scales.x.ticks.color = textColor.dark;
        chart.options.scales.y.ticks.color = textColor.dark;
        chart.options.scales.y.grid.color = gridColor.dark;
        chart.options.plugins.tooltip.bodyColor = tooltipBodyColor.dark;
        chart.options.plugins.tooltip.backgroundColor = tooltipBgColor.dark;
        chart.options.plugins.tooltip.borderColor = tooltipBorderColor.dark;
      } else {
        chart.options.scales.x.ticks.color = textColor.light;
        chart.options.scales.y.ticks.color = textColor.light;
        chart.options.scales.y.grid.color = gridColor.light;
        chart.options.plugins.tooltip.bodyColor = tooltipBodyColor.light;
        chart.options.plugins.tooltip.backgroundColor = tooltipBgColor.light;
        chart.options.plugins.tooltip.borderColor = tooltipBorderColor.light;
      }

      chart.update('none');
    } catch (error) {
      // Chart might have been destroyed, ignore
    }
  }, [currentTheme]);

  return (
    <React.Fragment>
      {/* Chart built with Chart.js 3 */}
      <div className="grow mt-5">
        <canvas ref={canvas} width={width} height={height}></canvas>
      </div>
    </React.Fragment>
  );
}

export default LineChart02;
